import { ROTA_APIS } from "@/api_framework/api_config";
import { useConfiguration } from "@/app/hooks/useConfiguration";
import { useCurlLogger } from "@/app/hooks/useCurlLogger";
import { useAPIUtils } from "@/app/hooks/useAPIUtils";
import axios, { AxiosError, AxiosResponse } from "axios";
import { useCallback, useState } from "react";
import toast from "react-hot-toast";
import AxiosCurlirize from "axios-curlirize";

AxiosCurlirize(axios, () => {});

// Rows come in the v2 shape (one clinic, day, advance day and persona) until the
// backend's BOOKING_FEE_CONFIG_SOURCE is "v3", and in the v3 shape (lists, and
// days_of_week mapping each day to its advance days) after it.
export interface IBookingFeeConfig {
  configuration_id: string;
  sub_service_id: string;
  subservice_name?: string;
  booking_fee_amount: number;
  vms_on_off_status: boolean;
  app_on_off_status: boolean;
  excluded_user_personas: string[];
  is_active?: boolean;
  created_at?: number;
  created_by?: string;
  updated_at?: number;
  updated_by?: string;
  // v2 shape
  clinic_id?: string;
  clinic_name?: string;
  day_of_week?: string;
  advance_days_for_booking_fee_applicability?: number;
  included_user_persona?: string;
  // v3 shape
  clinic_ids?: string[];
  clinic_names?: (string | null)[];
  days_of_week?: Record<string, number[]>;
  included_user_personas?: string[];
  required_user_personas?: string[];
}

export interface IBookingFeeConfigListFilters {
  clinic_id?: string;
  sub_service_id?: string;
  day_of_week?: string;
  included_user_persona?: string;
}

export const BOOKING_FEE_CONFIG_PAGE_SIZE = 20;

export const useBookingFeeConfigListAPI = () => {
  const { sendCurl } = useCurlLogger();
  const { captureError } = useAPIUtils();
  const configuration = useConfiguration();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [configList, setConfigList] = useState<IBookingFeeConfig[]>([]);
  const [total, setTotal] = useState<number>(0);

  const execute = useCallback(
    async (filters: IBookingFeeConfigListFilters = {}, offset = 0) => {
      setIsLoading(true);
      try {
        const accessToken = localStorage.getItem(configuration.localStorage.ACCESS_TOKEN.key);
        const params: Record<string, string | number> = {
          limit: BOOKING_FEE_CONFIG_PAGE_SIZE,
          offset,
        };
        if (filters.clinic_id) params.clinic_id = filters.clinic_id;
        if (filters.sub_service_id) params.sub_service_id = filters.sub_service_id;
        if (filters.day_of_week) params.day_of_week = filters.day_of_week;
        if (filters.included_user_persona) params.included_user_persona = filters.included_user_persona;

        const response = await axios
          .get(`${ROTA_APIS?.ROTA_BOOKING_FEE_CONFIG_LIST?.baseURL}`, {
            headers: {
              Authorization: `${accessToken}`,
              "Content-Type": "application/json",
            },
            params,
          })
          .then(
            (
              res: AxiosResponse<{
                status: boolean;
                message: string;
                data: { total: number; configurations: IBookingFeeConfig[] };
              }>,
            ) => {
              const list = res.data?.data?.configurations ?? [];
              // offset 0 = fresh page/filter change (replace); otherwise append next page.
              setConfigList((prev) => (offset > 0 ? [...prev, ...list] : list));
              setTotal(res.data?.data?.total ?? list.length);
              return list;
            },
          )
          .catch((e: AxiosError) => {
            sendCurl(e.config["curlCommand"]);
            captureError(e);
            toast.error("Error fetching booking fee configurations.");
            if (offset === 0) {
              setConfigList([]);
              setTotal(0);
            }
            return [];
          });
        return response;
      } catch (e: any) {
        toast.error("Server Error: " + e.message);
        setConfigList([]);
        return [];
      } finally {
        setIsLoading(false);
      }
    },
    [configuration, sendCurl, captureError],
  );

  return { execute, isLoading, configList, total };
};
